/**
 * client/src/lib/team-members-api.ts
 *
 * Team-member fast-path API client (AC-14) — thin barrel (300-Line
 * Modularity Rule, code-style.md). Implementation split across:
 *  - team-members-admin-grant-api.ts — org admin-grant ceremony
 *    (initiate/approve/decline/list/accept) + existing-member role change.
 *  - former-invites-api.ts — the former-invites lifecycle-trail read
 *    (#1781 task 3, AC-10).
 *  - this file — the membership-request fast path itself.
 *
 * Re-exports everything so existing `@/lib/team-members-api` imports
 * (admin-grant-panel.tsx, member-role-select.tsx, org-members.tsx, ...) keep
 * working unchanged.
 */
import type { AuthenticationResponseJSON } from "@simplewebauthn/browser";
import type { OrgMemberRole } from "@shared/org-types";
import { apiRequest, postJSON } from "./queryClient";

export * from "./team-members-admin-grant-api";
export * from "./former-invites-api";

/** Sends a membership request to an organisation for the supplied username and role. */
export async function requestMembership(
  orgId: string,
  username: string,
  role: OrgMemberRole,
  assertionResponse?: AuthenticationResponseJSON,
): Promise<void> {
  await postJSON<{ status: string }>(`/api/orgs/${orgId}/membership-requests`, {
    username,
    role,
    ...(assertionResponse ? { assertionResponse } : {}),
  });
}

export const MEMBERSHIP_REQUEST_SENT_COPY =
  "Request sent — they'll appear here once they accept.";

export interface MembershipRequestDTO {
  id: string;
  targetUserId: string;
  /**
   * The target person's own name/@handle, LEFT-joined server-side. Optional
   * + nullable on purpose: an older deployment omits both fields, and an
   * unresolvable profile returns null — the UI falls back to an honest
   * "Unknown member" in either case, never a raw UUID.
   */
  targetDisplayName?: string | null;
  targetUsername?: string | null;
  role: OrgMemberRole;
  status: "pending" | "accepted" | "declined" | "revoked";
  createdAt: string;
}

/** Lists lightweight member requests for the organisation's administrators. */
export function listMembershipRequests(orgId: string): Promise<{ requests: MembershipRequestDTO[] }> {
  return apiRequest(`/api/orgs/${orgId}/membership-requests`);
}

/**
 * POST /api/orgs/:orgId/membership-requests/:requestId/revoke — cancel a
 * still-`pending` lightweight membership request (module 01, revoke action).
 * Admin-only server-side (`isOrgAdmin`); a concurrent accept/decline races the
 * same `status='pending'` FOR-UPDATE guard as the read, so the server returns
 * a uniform 404 `NOT_FOUND` for "never existed" AND "no longer pending" alike
 * — there is no separate 409 here (unlike the OTP invite revoke route).
 */
export function revokeMembershipRequest(
  orgId: string,
  requestId: string,
): Promise<{ request: MembershipRequestDTO }> {
  return postJSON(`/api/orgs/${orgId}/membership-requests/${requestId}/revoke`, {});
}

// ---------------------------------------------------------------------------
// The INVITED person's own side of the same rail.
//
// `listMembershipRequests` above is the inviting org's admin-gated view. The
// three below are the target's: their own pending inbox, plus the accept and
// decline routes that were already live server-side but had no caller.
// ---------------------------------------------------------------------------

/** One pending org membership request awaiting the caller's own consent. */
export interface IncomingMembershipRequestDTO {
  id: string;
  orgId: string;
  orgName: string;
  role: OrgMemberRole;
  /**
   * The inviting administrator's display name, LEFT-joined server-side and
   * null when it cannot be resolved — the UI says "an administrator" rather
   * than dropping the invitation or printing a raw UUID.
   */
  requestedByDisplayName: string | null;
  createdAt: string; // ISO-8601 UTC
}

/** GET /api/membership-requests/incoming-pending */
export function getIncomingMembershipRequests(): Promise<{ requests: IncomingMembershipRequestDTO[] }> {
  return apiRequest("/api/membership-requests/incoming-pending");
}

/**
 * POST /api/membership-requests/:requestId/accept — the target's own
 * self-consent (Rule 16 is satisfied by construction: the accepter IS the
 * request's `target_user_id`). An `administrator` request additionally
 * requires its linked admin-grant ceremony to be approved and live, so this
 * can honestly 409 `ADMIN_GRANT_NOT_APPROVED` / `ADMIN_GRANT_HOLD_ACTIVE`.
 */
export function acceptMembershipRequest(
  requestId: string,
): Promise<{ membership: { id: string }; certificateId: string }> {
  return postJSON(`/api/membership-requests/${requestId}/accept`, {});
}

/** POST /api/membership-requests/:requestId/decline */
export function declineMembershipRequest(
  requestId: string,
): Promise<{ request: MembershipRequestDTO }> {
  return postJSON(`/api/membership-requests/${requestId}/decline`, {});
}
